"use client";

import React, { useState, useEffect } from "react";
import styles from "./CoreNav.module.scss";
import type { FeatureData } from "@/services/featureListing";
import { useScrollDirection } from "@/hooks/useScrollDirection";

interface CoreNavProps {
  cores: FeatureData[];
  label?: string | null;
  placeholder?: string | null;
}

/**
 * CoreNav - Sticky navigation to jump between core types on the page
 * Adjusts sticky position based on scroll direction (accounts for retracting header)
 */
const CoreNav: React.FC<CoreNavProps> = ({ cores, label, placeholder }) => {
  const [activeCore, setActiveCore] = useState<string>("");
  const { isScrollingUp, isAtTop } = useScrollDirection();

  // DoorTypeNav heights: 73px (its height), plus header 97px when visible
  // When scrolling up: header (97px) + DoorTypeNav (73px) = 170px, minus ~5px overlap = 165px
  // When scrolling down: just DoorTypeNav (73px) at top 0, so 73px
  const topOffset = isScrollingUp || isAtTop ? 160 : 60;

  useEffect(() => {
    // Set up intersection observer to highlight active core in nav
    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -70% 0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute("id");
          if (id) setActiveCore(id);
        }
      });
    }, observerOptions);

    // Observe all core card elements
    cores.forEach((core) => {
      const element = document.getElementById(core.anchorId);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [cores]);

  const scrollToCore = (anchorId: string) => {
    const element = document.getElementById(anchorId);
    if (element) {
      // Account for both sticky navs - use dynamic offset based on scroll direction
      const navOffset = isScrollingUp || isAtTop ? 230 : 125; // 165+70 or 73+62
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  if (cores.length === 0) {
    return null;
  }

  return (
    <nav
      className={styles.coreNav}
      aria-label="Core type navigation"
      style={{ top: `${topOffset}px` }}
    >
      <div className={`${styles.container} container mx-auto`}>
        <div className={styles.label}>{label || "Jump to:"}</div>
        <div className={styles.selectWrapper}>
          <select
            className={styles.select}
            aria-label={label || "Jump to"}
            value={activeCore}
            onChange={(event) => {
              const anchorId = event.target.value;
              if (anchorId) {
                scrollToCore(anchorId);
              }
            }}
          >
            <option value="">{placeholder || "Select an option"}</option>
            {cores.map((core) => (
              <option key={core.anchorId} value={core.anchorId}>
                {core.title}
              </option>
            ))}
          </select>
          <span className={styles.selectIcon} aria-hidden="true">
            <svg viewBox="0 0 20 20" focusable="false">
              <path d="m5.5 7.5 4.5 5 4.5-5" />
            </svg>
          </span>
        </div>
      </div>
    </nav>
  );
};

export default CoreNav;
